import {
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Margin } from '@components/patterns/LineChart/types';

const DEFAULT_MARGINS: Margin = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export function useMargin(
  ref: RefObject<SVGSVGElement>,
  defaultMargins?: Partial<Margin>
) {
  const [margins, setMargins] = useState<Partial<Margin>>({});

  const xAxisListenerRef = useRef<(() => void) | null>(null);
  const unsubscribeXAxis = useCallback(() => {
    xAxisListenerRef.current?.();
    xAxisListenerRef.current = null;
  }, []);
  const subscribeXAxis = useCallback(
    (xAxis: Element) => {
      unsubscribeXAxis();

      const observer = new ResizeObserver((entries) => {
        const bottom = entries[0].contentRect.height + 10;
        setMargins((prev) => ({ ...prev, bottom }));
      });
      observer.observe(xAxis);

      xAxisListenerRef.current = () => {
        observer.disconnect();
        setMargins((prev) => ({ ...prev, bottom: undefined }));
      };
    },
    [unsubscribeXAxis]
  );

  const yAxisListenerRef = useRef<(() => void) | null>(null);
  const unsubscribeYAxis = useCallback(() => {
    yAxisListenerRef.current?.();
    yAxisListenerRef.current = null;
  }, []);
  const subscribeYAxis = useCallback(
    (yAxis: Element) => {
      unsubscribeYAxis();

      const observer = new ResizeObserver((entries) => {
        const left = entries[0].contentRect.width + 10;
        setMargins((prev) => ({ ...prev, left }));
      });
      observer.observe(yAxis);

      yAxisListenerRef.current = () => {
        observer.disconnect();
        setMargins((prev) => ({ ...prev, left: undefined }));
      };
    },
    [unsubscribeYAxis]
  );

  useLayoutEffect(() => {
    const svg = ref.current;
    if (!svg) return;

    // If the x axis is already mounted, subscribe to it
    const xAxis = svg.querySelector('.x-axis');
    if (xAxis) {
      subscribeXAxis(xAxis);
    }

    // If the y axis is already mounted, subscribe to it
    const yAxis = svg.querySelector('.y-axis');
    if (yAxis) {
      subscribeYAxis(yAxis);
    }

    // Observe for changes to the DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof SVGElement)) return;
          if (node.matches('.x-axis')) {
            subscribeXAxis(node);
          } else if (node.matches('.y-axis')) {
            subscribeYAxis(node);
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof SVGElement)) return;
          if (node.matches('.x-axis')) {
            unsubscribeXAxis();
          } else if (node.matches('.y-axis')) {
            unsubscribeYAxis();
          }
        });
      }
    });
    observer.observe(svg, { childList: true });

    return () => {
      observer.disconnect();
      unsubscribeXAxis();
      unsubscribeYAxis();
    };
  }, [unsubscribeXAxis, unsubscribeYAxis, subscribeXAxis, subscribeYAxis]);

  const computedMargins = useMemo(() => {
    const top = margins.top ?? defaultMargins?.top ?? DEFAULT_MARGINS.top;
    const bottom =
      margins.bottom ?? defaultMargins?.bottom ?? DEFAULT_MARGINS.bottom;

    const left = margins.left ?? defaultMargins?.left ?? DEFAULT_MARGINS.left;
    const right =
      margins.right ?? defaultMargins?.right ?? DEFAULT_MARGINS.right;

    return { top, right, bottom, left };
  }, [
    defaultMargins?.top,
    defaultMargins?.right,
    defaultMargins?.bottom,
    defaultMargins?.left,
    margins,
  ]);

  return computedMargins;
}
